import { Col, Row, Text, TouchField, Button, modal, showPopupMessage, InputField, SelectField } from 'components';
import React, { useState, useEffect, useMemo } from 'react';
import { TCMSOrder } from 'type';
import Store, { Api } from 'store';
import { COLOR, LOG_TYPES, SHIPPING_SERVICES } from 'const';
import { useWindowDimensions, ScrollView, Modal, View } from 'react-native';
import { EvilIcons } from '@expo/vector-icons';
import { ORDER_STAGE, ORDER_STATUS } from 'store/Order.Store';
import { omitBy, isNil } from 'lodash';
import { commonFuncs, normalizeCommaSeparatedList, parseCommaSeparatedList } from '../ListOrders.funcs';
import { getDefaultShippingService } from '../utils';

export const AdminOverrideOrderInfo = ({ order, onSuccess }: { order: TCMSOrder, onSuccess?: () => void }) => {
  const OrderStore = Store.useOrderStore();
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingUrl, setTrackingUrl] = useState('');
  const [isMultiTrackingMode, setIsMultiTrackingMode] = useState(false);
  const [lineItemTrackings, setLineItemTrackings] = useState<Array<{
    lineItemId: number | string;
    name: string;
    quantity: number;
    trackingNumber: string;
    trackingUrl: string;
  }>>([]);
  const [rmOrderIdentifier, setRmOrderIdentifier] = useState('');
  const [shippingMethod, setShippingMethod] = useState('');
  const { width, height } = useWindowDimensions();
  const [status, setStatus] = useState('');
  const [stage, setStage] = useState('');
  const [dispatchedDate, setDispatchedDate] = useState<string>('');

  const [isSaving, setIsSaving] = useState(false);
  const [showMultiTrackingModal, setShowMultiTrackingModal] = useState(false);
  const [draftLineItemTrackings, setDraftLineItemTrackings] = useState<typeof lineItemTrackings>([]);

  const buildNormalizedFromRows = (rows: typeof lineItemTrackings) => {
    const validRows = rows.filter(row => row.trackingNumber?.trim() || row.trackingUrl?.trim());
    const normalizedTracking = normalizeCommaSeparatedList(
      validRows.map(v => v.trackingNumber?.trim()).filter(Boolean).join(',')
    );
    const normalizedUrl = normalizeCommaSeparatedList(
      validRows.map(v => v.trackingUrl?.trim()).filter(Boolean).join(',')
    );
    return { validRows, normalizedTracking, normalizedUrl };
  };

  const deleteOrder = async (val) => {
    const success = await commonFuncs.onDelete([val]);
    if (!success) return;
    onSuccess?.();
  }

  const statusOptions = ORDER_STATUS.map(v => ({ label: v, value: v }));
  const stageOptions = ORDER_STAGE.map(v => ({ label: v, value: v }));

  const isITLuggage = order['Store ID'] === '123415927657' || order['Client ID'] === '222415927656';
  const shippingMethodsOptions = Object.keys(SHIPPING_SERVICES).map(v => {
    const baseName = SHIPPING_SERVICES[v].name;
    const label = isITLuggage && v === 'DPD' ? 'DPD' : baseName;
    return { label, value: v };
  });

  const shopifyDeleted = useMemo(() => {
    const last = order.Pipelines?.[order.Pipelines?.length - 1];
    if (!last) return false;
    return last.SharedData?.shopifyDeleted;
  }, [order])

  const canBeProcessedItems = useMemo(() => {
    const pipelines = order?.Pipelines;
    if (!pipelines?.length) return [];
    return pipelines[pipelines.length - 1]?.SharedData?.canBeProcessedItems || [];
  }, [order]);
  const canUseMultiTrackingMode = canBeProcessedItems.length > 1;

  useEffect(() => {
    const last = order.Pipelines[order.Pipelines.length - 1];
    setShippingMethod(getDefaultShippingService(order));
    if (last && last.SharedData && last.SharedData.dispatchedDate) {
      setDispatchedDate(last.SharedData.dispatchedDate);
    }
    // if (last && last.SharedData && last.SharedData.trackingNumber) {
    //   setTrackingNumber(last.SharedData.trackingNumber);
    //   return;
    // }
    if (!!last?.SharedData?.trackingNumber) {
      setTrackingNumber(last.SharedData.trackingNumber);
    }
    if (!!last?.SharedData?.trackingUrl) {
      setTrackingUrl(last.SharedData.trackingUrl);
    }
    const trackingNumbers = parseCommaSeparatedList(last?.SharedData?.trackingNumber || '');
    const trackingUrls = parseCommaSeparatedList(last?.SharedData?.trackingUrl || '');
    setLineItemTrackings(
      canBeProcessedItems.map((item, idx) => ({
        lineItemId: item?.id,
        name: item?.name || String(item?.id || ''),
        quantity: item?.quantity || 1,
        trackingNumber: trackingNumbers[idx] || '',
        trackingUrl: trackingUrls[idx] || '',
      }))
    );
    setIsMultiTrackingMode(canBeProcessedItems.length > 1 && (trackingNumbers.length > 1 || trackingUrls.length > 1));
    if (!!last?.SharedData?.royalMailOrderIdentifier) {
      setRmOrderIdentifier(last.SharedData.royalMailOrderIdentifier);
    }
    if (order.StageStatus) setStatus(order.StageStatus);
    if (order.Stage) setStage(order.Stage);
  }, [order, canBeProcessedItems]);

  const save = async () => {
    setIsSaving(true);
    try {
      const useMultiTrackingMode = isMultiTrackingMode && canUseMultiTrackingMode;
      const trackingPackages = useMultiTrackingMode
        ? lineItemTrackings
          .filter(row => row.trackingNumber?.trim() || row.trackingUrl?.trim())
          .map(row => ({
            trackingNumber: row.trackingNumber?.trim(),
            trackingUrl: row.trackingUrl?.trim() || undefined,
            lineItems: [
              {
                id: row.lineItemId,
                quantity: row.quantity || 1,
              }
            ],
          }))
        : [];
      const normalizedTracking = useMultiTrackingMode
        ? normalizeCommaSeparatedList(trackingPackages.map(v => v.trackingNumber).filter(Boolean).join(','))
        : normalizeCommaSeparatedList(trackingNumber);
      const normalizedUrl = useMultiTrackingMode
        ? normalizeCommaSeparatedList(trackingPackages.map(v => v.trackingUrl).filter(Boolean).join(','))
        : normalizeCommaSeparatedList(trackingUrl);
      if (status || stage) {
        await OrderStore.updateOrderStage([order], omitBy({
          Stage: stage,
          StageStatus: status,
        }, isNil));
      }
      const obj = {
        shippingMethod: shippingMethod || undefined,
        trackingNumber: normalizedTracking || undefined,
        trackingUrl: normalizedUrl || undefined,
        dispatchedDate: dispatchedDate || undefined,
        royalMailOrderIdentifier: rmOrderIdentifier || undefined,
      };
      if (shippingMethod && shippingMethod !== order?.['Other Data']?.shippingService) {
        await Api.Order.updateShippingStripeInvoice({
          orderId: String(order['Order ID']),
          shippingService: shippingMethod,
        })
      }
      const atLeastOneKeyNotUndefiend = Object.keys(obj).some(key => obj[key] !== undefined);
      if (atLeastOneKeyNotUndefiend) await OrderStore.updateShareData(order, obj);
      const last = order.Pipelines?.[order.Pipelines.length - 1];
      const firstTracking = parseCommaSeparatedList(normalizedTracking)[0];
      const firstUrl = parseCommaSeparatedList(normalizedUrl)[0];
      const trackingChanged = normalizedTracking !== last?.SharedData?.trackingNumber;
      const trackingUrlChanged = normalizedUrl !== last?.SharedData?.trackingUrl;
      if (firstTracking && (trackingChanged || trackingUrlChanged)
        && order['Store ID'] && last?.SharedData?.canBeProcessedItems?.length
        && !order['Raw Data']?.is_sample_request
      ) {
        const shopifyOrderPayload: any = {
          lineItems: last?.SharedData?.canBeProcessedItems,
          orderId: +order['Order Source ID'] || +order['Order ID'],
          storeId: order['Store ID'],
          trackingNumber: firstTracking,
          trackingUrl: firstUrl || undefined,
          ...(useMultiTrackingMode ? { trackingPackages } : {}),
        };
        const res: any = await Api.Shop.updateShopifyOrderStatus({
          orders: [shopifyOrderPayload]
        });
        const errors = res?.error || [];
        if (Array.isArray(errors) && errors.length > 0) {
          console.error(`Shopify update failed: ${errors.join(', ')}`);
        }
      }
      Api.Log.addLog({
        type: LOG_TYPES.OVERRIDE_ORDER,
        data: {
          orderId: order['Order ID'],
        },
        oldData: {
          stage: order.Stage,
          stageStatus: order.StageStatus,
          shippingMethod: last?.SharedData?.shippingMethod,
          trackingNumber: last?.SharedData?.trackingNumber,
          trackingUrl: last?.SharedData?.trackingUrl,
          dispatchedDate: last?.SharedData?.dispatchedDate,
          royalMailOrderIdentifier: last?.SharedData?.dispatchedDate,
        },
        newData: {
          stage,
          stageStatus: status,
          shippingMethod,
          trackingNumber: normalizedTracking,
          trackingUrl: normalizedUrl,
          dispatchedDate,
          royalMailOrderIdentifier: rmOrderIdentifier,
        }
      })
      setTrackingNumber(normalizedTracking);
      setTrackingUrl(normalizedUrl);
      onSuccess?.();
    } catch (err) {
      showPopupMessage({
        title: '',
        content: String(err),
        buttonOkText: 'OK',
        // 
        typeHighlight: 'danger',
        contentHighlight: 'Error'

      });
      // alert(String(err));
    }
    setIsSaving(false);
  }

  const openMultiTrackingModal = () => {
    setDraftLineItemTrackings([...lineItemTrackings]);
    setShowMultiTrackingModal(true);
  };

  const applyMultiTrackingModal = () => {
    const { validRows, normalizedTracking, normalizedUrl } = buildNormalizedFromRows(draftLineItemTrackings);
    setLineItemTrackings(draftLineItemTrackings);
    setIsMultiTrackingMode(validRows.length > 0);
    setTrackingNumber(normalizedTracking);
    setTrackingUrl(normalizedUrl);
    setShowMultiTrackingModal(false);
  };

  return (
    <Col round1 bgWhite shadow p2 onPress={() => { }} activeOpacity={1} width={width <= 600 ? width * 0.95 : width * 0.5} minHeight={width <= 600 ? height * 0.95 : 300}>
      <Modal
        visible={showMultiTrackingModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowMultiTrackingModal(false)}
      >
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.35)', justifyContent: 'center', alignItems: 'center' }}>
          <Col
            round1
            bgWhite
            shadow
            p2
            width={width <= 600 ? width * 0.95 : width * 0.7}
            height={width <= 600 ? height * 0.9 : height * 0.85}
            maxHeight={height * 0.9}
          >
            <Text h3 mb2>Multi tracking by line item</Text>
            <Text fontSize={12} color={COLOR.BORDER} mb2>
              Each row can map to one package tracking. Save to apply data back to Override modal.
            </Text>
            <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 12 }} showsVerticalScrollIndicator>
              <Col borderWidth={1} borderColor={COLOR.BORDER} borderRadius={8} p1>
                <Row mb1>
                  <Text flex={3} fontWeight="600">Line item</Text>
                  <Text flex={1} fontWeight="600">Qty</Text>
                  <Text flex={3} fontWeight="600">Tracking Number</Text>
                  <Text flex={5} fontWeight="600">Tracking Url</Text>
                </Row>
                {draftLineItemTrackings.map((row, idx) => (
                  <Row key={`${row.lineItemId}-${idx}`} alignItems="center" mb1>
                    <Text flex={3} numberOfLines={2}>{row.name}</Text>
                    <Text flex={1}>{row.quantity}</Text>
                    <Col flex={3} mr1>
                      <InputField
                        value={row.trackingNumber}
                        onChangeText={(val: string) => {
                          setDraftLineItemTrackings(prev => prev.map((item, i) => i === idx ? { ...item, trackingNumber: val } : item));
                        }}
                        inputProps={{ placeholder: 'Tracking no.' }}
                      />
                    </Col>
                    <Col flex={5}>
                      <InputField
                        value={row.trackingUrl}
                        onChangeText={(val: string) => {
                          setDraftLineItemTrackings(prev => prev.map((item, i) => i === idx ? { ...item, trackingUrl: val } : item));
                        }}
                        inputProps={{ placeholder: 'https://...' }}
                      />
                    </Col>
                  </Row>
                ))}
              </Col>
            </ScrollView>
            <Row justifyContent="flex-end">
              <Button
                outline
                text="Cancel"
                width={100}
                height={30}
                mr1
                onPress={() => setShowMultiTrackingModal(false)}
              />
              <Button
                text="Apply"
                width={100}
                height={30}
                onPress={applyMultiTrackingModal}
              />
            </Row>
          </Col>
        </View>
      </Modal>
      <Text h3 mb2>Order override</Text>
      <Col flex1>
        <InputField
          title="Royal Mail Order Identifier"
          value={rmOrderIdentifier}
          onChangeText={setRmOrderIdentifier}
          marginBottom={12}
        />
        <InputField
          title="Tracking Number"
          value={trackingNumber}
          onChangeText={setTrackingNumber}
          marginBottom={12}
          editable={!isMultiTrackingMode || !canUseMultiTrackingMode}
        />
        <InputField
          title="Tracking Url"
          value={trackingUrl}
          onChangeText={setTrackingUrl}
          marginBottom={8}
          editable={!isMultiTrackingMode || !canUseMultiTrackingMode}
        />
        {canUseMultiTrackingMode && (
          <Row alignItems="center" marginBottom={12}>
            <Button
              text={isMultiTrackingMode ? 'Edit multi tracking' : 'Multi tracking'}
              width={130}
              height={30}
              onPress={openMultiTrackingModal}
            />
          </Row>
        )}
        <SelectField
          title="Shipping Method"
          options={shippingMethodsOptions}
          value={shippingMethod}
          onChange={(val: string) => setShippingMethod(val)}
          marginBottom={12}
        />
        <InputField
          title="Dispatched Date"
          value={dispatchedDate}
          onChangeText={setDispatchedDate}
          marginBottom={12}
        />
        <SelectField
          title="Order Stage"
          options={stageOptions}
          value={stage}
          onChange={(val: string) => setStage(val)}
          marginBottom={12}
        />
        <SelectField
          title="Order Status"
          options={statusOptions}
          value={status}
          onChange={(val: string) => setStatus(val)}
          marginBottom={12}
        />
      </Col>
      {shopifyDeleted && <Text color="red" mb2>This order has been deleted on Shopify</Text>}
      <Row justifyContent={'space-between'}>
        <Col>
          <TouchField flexDirection={'row'} width={140} height={30} borderRadius={15} middle onPress={() => deleteOrder(order)}>
            <EvilIcons name="trash" size={24} color={COLOR.FONT} />
            <Text ml0>Delete order</Text>
          </TouchField>
        </Col>
        <Row>
          <Button
            isLoading={isSaving}
            text="Save"
            width={100}
            height={30}
            mr1
            onPress={save}
          />
          <Button
            outline
            text="Close"
            width={100}
            height={30}
            onPress={() => modal.hide()}
          />
        </Row>
      </Row>
    </Col>
  );
}
